import { Timeline } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  FaUserPlus,
  FaEdit,
  FaCheckCircle,
  FaUserCheck,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaTag,
  FaBuilding,
  FaPaperclip,
  FaTrash,
} from "react-icons/fa";

export default function LeadTimeline({ lead }) {
  const { t } = useTranslation();

  if (!lead) {
    return <div className="p-4 text-center text-gray-500">{t("lead.timeline.noActivities")}</div>;
  }

  const timelineItems = [];

  if (lead.activityLogs && Array.isArray(lead.activityLogs) && lead.activityLogs.length > 0) {
    lead.activityLogs.forEach((log) => {
      let color = "blue";
      let dot = <FaUserPlus className="text-blue-500" />;
      let title = "";

      switch (log.actionType) {
        case "created":
          color = "blue";
          dot = <FaUserPlus className="text-blue-500" />;
          title = t("lead.timeline.created");
          break;
        case "updated":
          color = "green";
          dot = <FaEdit className="text-green-500" />;
          title = t("lead.timeline.updated");
          break;
        case "status_changed":
          color = "orange";
          dot = <FaTag className="text-orange-500" />;
          title = t("lead.timeline.statusSet");
          break;
        case "attachment_added":
          color = "cyan";
          dot = <FaPaperclip className="text-cyan-500" />;
          title = t("lead.attachments") + " (Added)";
          break;
        case "attachment_deleted":
          color = "red";
          dot = <FaTrash className="text-red-500" />;
          title = t("lead.attachments") + " (Deleted)";
          break;
        case "converted":
          color = "purple";
          dot = <FaCheckCircle className="text-purple-500" />;
          title = t("lead.timeline.converted");
          break;
        default:
          color = "gray";
          dot = <FaEdit className="text-gray-500" />;
          title = "Activity";
      }

      timelineItems.push({
        color,
        dot,
        children: (
          <div className="pl-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              {title}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {log.description}
            </div>
            {log.userName && (
              <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                By: <span className="font-medium">{log.userName}</span>
              </div>
            )}
            <div className="text-sm text-gray-400 dark:text-gray-600 mt-1">
              {moment(log.createdAt || log.created_at).format("MMMM DD, YYYY [at] HH:mm")}
            </div>
          </div>
        ),
        timestamp: moment(log.createdAt || log.created_at).valueOf(),
      });
    });
  } else {
    // Fallback static timeline logic for old leads
    // Lead Created
    if (lead.createdAt) {
      timelineItems.push({
        color: "blue",
        dot: <FaUserPlus className="text-blue-500" />,
        children: (
          <div className="pl-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              {t("lead.timeline.created")}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {moment(lead.createdAt).format("MMMM DD, YYYY [at] HH:mm")}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              {t("lead.timeline.createdBy")}
            </div>
          </div>
        ),
        timestamp: moment(lead.createdAt).valueOf(),
      });
    }

    // Lead Updated
    if (lead.updatedAt && lead.updatedAt !== lead.createdAt) {
      timelineItems.push({
        color: "green",
        dot: <FaEdit className="text-green-500" />,
        children: (
          <div className="pl-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              {t("lead.timeline.updated")}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {moment(lead.updatedAt).format("MMMM DD, YYYY [at] HH:mm")}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              {t("lead.timeline.lastModified")}
            </div>
          </div>
        ),
        timestamp: moment(lead.updatedAt).valueOf(),
      });
    }

    // Lead Converted
    if (lead.isConverted === "true") {
      timelineItems.push({
        color: "purple",
        dot: <FaCheckCircle className="text-purple-500" />,
        children: (
          <div className="pl-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              {t("lead.timeline.converted")}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t("lead.timeline.convertedDescription")}
            </div>
          </div>
        ),
        timestamp: lead.updatedAt ? moment(lead.updatedAt).valueOf() : moment().valueOf(),
      });
    }

    // Lead Status Changes
    if (lead.leadStatus) {
      timelineItems.push({
        color: "orange",
        dot: <FaTag className="text-orange-500" />,
        children: (
          <div className="pl-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              {t("lead.timeline.statusSet")}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t("lead.timeline.currentStatus")}: <span className="font-medium">{lead.leadStatus}</span>
            </div>
          </div>
        ),
        timestamp: lead.updatedAt ? moment(lead.updatedAt).valueOf() : moment(lead.createdAt).valueOf(),
      });
    }

    // Contact Information Added
    if (lead.email || lead.phone || lead.mobile) {
      const contactInfo = [];
      if (lead.email) contactInfo.push("Email");
      if (lead.phone) contactInfo.push("Phone");
      if (lead.mobile) contactInfo.push("Mobile");

      timelineItems.push({
        color: "cyan",
        dot: <FaEnvelope className="text-cyan-500" />,
        children: (
          <div className="pl-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              {t("lead.timeline.contactInfoAdded")}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {contactInfo.join(", ")} {t("lead.timeline.added")}
            </div>
          </div>
        ),
        timestamp: lead.createdAt ? moment(lead.createdAt).valueOf() : moment().valueOf(),
      });
    }

    // Location Information
    if (lead.country || lead.nationality) {
      timelineItems.push({
        color: "geekblue",
        dot: <FaMapMarkerAlt className="text-blue-600" />,
        children: (
          <div className="pl-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              {t("lead.timeline.locationInfo")}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {lead.country && <span>{t("lead.country")}: {lead.country}</span>}
              {lead.country && lead.nationality && <span className="mx-2">•</span>}
              {lead.nationality && <span>{t("lead.nationality")}: {lead.nationality}</span>}
            </div>
          </div>
        ),
        timestamp: lead.createdAt ? moment(lead.createdAt).valueOf() : moment().valueOf(),
      });
    }

    // Agent/Company Assignment
    if (lead.agentId) {
      timelineItems.push({
        color: "gold",
        dot: <FaBuilding className="text-yellow-500" />,
        children: (
          <div className="pl-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              {t("lead.timeline.agentAssigned")}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t("lead.timeline.agentAssignedDescription")}
            </div>
          </div>
        ),
        timestamp: lead.updatedAt ? moment(lead.updatedAt).valueOf() : moment(lead.createdAt).valueOf(),
      });
    }
  }

  // Sort by timestamp (newest first)
  timelineItems.sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
          {t("lead.timeline.title")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t("lead.timeline.description")}
        </p>
      </div>

      {timelineItems.length > 0 ? (
        <Timeline
          mode="left"
          items={timelineItems}
          className="lead-timeline"
        />
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          {t("lead.timeline.noActivities")}
        </div>
      )}
    </div>
  );
}

